import React, {useContext, useMemo, useRef} from 'react';

import objectHash from 'object-hash';

import {usePushNotification} from '@pb/kit-component-push-notification';
import {TableInteractions, useTableInteractionsAPI} from '@pb/kit-component-table-interactions';
import {GlobalHistoryContext} from '@pb/kit-context-global-history';
import {LocaleContext} from '@pb/kit-context-locale';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

import {createDataStore, updateDataStore} from './get-store-data';
import {useUpdateTable} from './use-update-table';

import type {
    InteractiveColumn,
    OnValidate,
    ValidateColumnValue,
} from './typedef';
import type {RowData} from '@pb/kit-component-data-table-v2';
import type {
    Store,
    UseApi,
    UseLiveUpdate,
    UseTracking,
} from '@pb/kit-component-table-interactions';


type Props = {
    children: (dataRows: RowData[], columns: InteractiveColumn[]) => React.ReactNode,
    dataTableId: string,
    columns: InteractiveColumn[],
    dataRows: RowData[],
    dataAPI: UseApi,
    trackingAPI: UseTracking,
    validator: ValidateColumnValue,
    onValidate: OnValidate,
};

export const WithInteractionTable = (props: Props) => {
    const {
        dataTableId,
        columns,
        dataRows,
        children,
        dataAPI,
        trackingAPI,
        validator,
        onValidate,
    } = props;

    const {GlobalHistory} = useContext(GlobalHistoryContext);
    const {locale} = useContext(LocaleContext);

    const pushNotification = usePushNotification();
    const {tableInteractionsAPI} = useTableInteractionsAPI();
    const dataLayer = useRef<Store>([]);

    const useApi = useCallbackWithRef<UseApi>(dataAPI);
    const useTracking = useCallbackWithRef<UseTracking>(trackingAPI);
    const useLiveUpdate = useCallbackWithRef<UseLiveUpdate>(() => ({
        onSendUpdate: () => undefined,
    }));
    const {
        key,
        entityIds,
        columnsIds,
        allColumnsIds,
        interactionColumnsIds,
    } = useUpdateTable({columns, dataRows});

    useMemo(() => {
        const newKey = objectHash({allColumnsIds});

        if (key.current !== newKey) {
            key.current = newKey;
            dataLayer.current = updateDataStore({
                columns,
                dataRows,
                locale,
                store: dataLayer.current,
                validator,
                onValidate,
            });
        }
    }, [allColumnsIds, dataRows, columns, locale, key, onValidate, validator]);

    useMemo(() => {
        dataLayer.current = createDataStore({columns, dataRows, locale, validator, onValidate});
    }, [dataRows, columns, locale, onValidate, validator]);

    return (
        <TableInteractions {...{
            ...pushNotification,
            data: dataLayer.current,
            dataTableId,
            useApi,
            useTracking,
            useLiveUpdate,
            getGlobalHistory: GlobalHistory.getHistory,
            setGlobalHistory: GlobalHistory.setHistory,
            tableInteractionsAPI,
            entityIds,
            columnsIds,
            interactionColumnsIds,
        }}>
            {children(dataRows, columns)}
        </TableInteractions>
    );
};
